// src/lib/signoffs.js
// STATUS: active | UPDATED: 2026-08-29
// LOG:
//   2026-08-29  New. Manager sign-off, the third leg of the governing test.
//
// Sign-off is the point of the whole loop.
//
// step_progress.status runs in_progress -> check_passed -> signed_off. The app
// has always been able to reach check_passed and stop, so manager_signoffs has
// never held a row and a manager has never had a reason to open the product.
// "Help a manager inspect it" is one of the four things page 19 says a feature
// must do, and it was the missing one.
//
// NO NETLIFY FUNCTION HERE, ON PURPOSE
//
// The permission model is already in the database and it is exactly right:
//
//   signoffs_self_insert     user_id = auth.uid() AND status = 'requested'
//   signoffs_manager_update  current_user_manages(user_id)
//   signoffs_manager_select  current_user_manages(user_id)
//   signoffs_self_select     user_id = auth.uid()
//
// A salesperson can ask and cannot approve. A manager can only decide for their
// own floor. Wrapping that in a function would mean re-implementing those four
// rules in JavaScript and hoping the two agree. The database is the better
// place for a rule that must never be wrong.
//
// Approving does not write step_progress from here either. Managers hold SELECT
// on that table and nothing more, so a trigger does it, in the same
// transaction. See supabase/migrations/0008.

import { supabase } from './supabase'
import { STEPS } from '../content/steps'

// A step is only sign-off-able once the knowledge check is genuinely passed,
// and that status now comes from the server since grading moved there.
export const SIGNOFF_ELIGIBLE = 'check_passed'

// steps are numbered 1..13 everywhere in the app and keyed by uuid in the
// database. Resolved here, once, and cached, so no caller has to hold both.
let stepIdByNumber = null

async function resolveStepIds() {
  if (stepIdByNumber) return stepIdByNumber
  const { data, error } = await supabase.from('steps').select('id, number')
  if (error || !data) return null
  stepIdByNumber = {}
  for (const row of data) stepIdByNumber[row.number] = row.id
  return stepIdByNumber
}

export async function requestSignoff(userId, stepNumber) {
  const map = await resolveStepIds()
  const stepId = map && map[stepNumber]
  if (!stepId) return { ok: false, error: 'Could not find that step. Try again in a moment.' }

  // Through the function rather than straight to the table, because asking to
  // be signed off has to reach a human. The insert still runs under the
  // caller's own token and the same signoffs_self_insert policy; what the
  // browser cannot do is look up who manages this floor, and it must never be
  // handed a list of manager addresses.
  //
  // Falls back to the direct insert if the function cannot be reached, because
  // a recorded request with no email beats no request at all.
  const step = STEPS.find((x) => x.number === stepNumber)
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (session?.access_token) {
    try {
      const res = await fetch('/.netlify/functions/request-signoff', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ stepId, stepNumber, stepTitle: step?.title || '' }),
      })
      const out = await res.json()
      if (res.ok && out.ok) {
        return { ok: true, signoff: out.signoff, alreadyRequested: !!out.alreadyRequested, notified: out.notified }
      }
      if (res.ok && out.error) return { ok: false, error: out.error }
    } catch {
      // fall through to the direct insert
    }
  }

  const { data, error } = await supabase
    .from('manager_signoffs')
    .insert({ user_id: userId, step_id: stepId, status: 'requested' })
    .select()
    .single()

  if (error) {
    if (error.code === '23505') {
      return { ok: true, alreadyRequested: true }
    }
    return { ok: false, error: error.message }
  }
  return { ok: true, signoff: data }
}

// Every sign-off row this person can see. RLS decides whether that means their
// own or their whole floor, which is why there is no role check here.
export async function listSignoffs() {
  const { data, error } = await supabase
    .from('manager_signoffs')
    .select('id, user_id, step_id, status, notes, created_at, decided_at, manager_id')
    .order('created_at', { ascending: false })

  if (error) return { ok: false, error: error.message, signoffs: [] }

  // Hand back step numbers as well, so callers never deal in uuids.
  const map = await resolveStepIds()
  const numberById = {}
  for (const [n, id] of Object.entries(map || {})) numberById[id] = Number(n)

  return {
    ok: true,
    signoffs: (data || []).map((r) => ({ ...r, stepNumber: numberById[r.step_id] ?? null })),
  }
}

// approve or redo. manager_id and decided_at are stamped by the trigger rather
// than sent from here, so who signed and when cannot be posted by hand.
export async function decideSignoff(signoffId, status, notes) {
  if (status !== 'approved' && status !== 'redo') {
    return { ok: false, error: 'A sign-off is either approved or sent back for another rep.' }
  }

  const patch = { status }
  if (typeof notes === 'string' && notes.trim()) patch.notes = notes.trim().slice(0, 500)

  const { data, error } = await supabase
    .from('manager_signoffs')
    .update(patch)
    .eq('id', signoffId)
    .select()
    .single()

  if (error) return { ok: false, error: error.message }
  return { ok: true, signoff: data }
}
